import React, { useContext, useRef, useState, useMemo } from 'react';
import { Column, TableInstance } from 'react-table';
import { Button } from 'react-bootstrap';
import { ResponseError } from '@nintendo/forms';
import { useMsal } from '@azure/msal-react';
import { RoleUtils } from '@galaxy/lorule-react-commons';
import { getProjectActuals } from '../../../api/projectBalanceApi';
import { ProjectActualItem } from '../../../api/projectBalanceApiTypes';
import ConfigContext from '../../../contexts/ConfigContext';
import useAsyncWithState from '../../../hooks/useAsyncWithState';
import DataTable from '../../common/datatables/DataTable';
import Spinner from '../../common/Spinner';
import ProjectInvoiceModal from '../modal/ProjectInvoiceModal';
import { exportActualsList } from '../../common/listUtils';
import { formatCurrency, renderDateCell } from '../../../utils/displayUtils';
import { MAX_COUNT, DEFAULT_INITIAL_STATE } from '../../../utils/Constants';
import ActualsFilter, { ActualsSearchFormType } from './ActualsFilter';
import useStateWithSessionStorage from '../../../hooks/useStateWithSessionStorage';

const INVOICE_TRANSACTION_SOURCES = ['AP INVOICE', 'AP NRTax', 'AP Expense', 'NIN Payables'];

interface ActualsProps {
    amId: number;
    amNumber: number;
    total: number | null;
}

const Actuals = (props: ActualsProps) => {
    const { instance } = useMsal();
    const roles = RoleUtils.getRoles(instance);
    const { tableauDashboardUrl } = window;

    const config = useContext(ConfigContext);
    const { amId, amNumber } = props;

    const [search, setSearch] = useStateWithSessionStorage(`'actualsSearchObject${amId}`, '{}');
    const [initialState, setInitialState] = useStateWithSessionStorage(
        `actualsInitialState${amId}`,
        JSON.stringify(DEFAULT_INITIAL_STATE),
    );
    const [refresh, setRefresh] = useState(0);

    const { dateFrom, dateTo, transactionSources, textSearchValues } = JSON.parse(
        search,
    ) as ActualsSearchFormType;

    const {
        value: actuals,
        loading,
        error,
    } = useAsyncWithState(
        () =>
            getProjectActuals(
                config,
                amId,
                {
                    dateFrom,
                    dateTo,
                    sources: transactionSources,
                    textSearchValues: textSearchValues ? textSearchValues.split(' ') : undefined,
                },
                MAX_COUNT,
                null,
                null,
                null,
                !RoleUtils.checkRole(roles, ['ActionMemo.Admin']),
            ),
        [] as ProjectActualItem[],
        [refresh, search],
    );

    const tableInstanceRef = useRef<TableInstance<ProjectActualItem> | null>(null);
    const [tableRefReady, setTableRefReady] = useState(false);

    const refreshFunc = () => {
        setRefresh(refresh + 1);
    };

    const columns = React.useMemo(
        () => [
            {
                Header: 'Date',
                accessor: 'date',
                Cell: renderDateCell,
            },
            {
                Header: 'Source',
                accessor: 'source',
                width: null,
            },
            {
                Header: 'Transaction #',
                accessor: 'documentNumber',
                width: null,
            },
            {
                Header: 'Vendor',
                accessor: 'vendorName',
                width: null,
            },
            {
                Header: 'Amount',
                accessor: 'amount',
                Cell: (cellProps: { value: number }) =>
                    useMemo(() => {
                        const absValue = Math.abs(cellProps.value);
                        const negativeValue = `(${formatCurrency(absValue, 2)})`;
                        return (
                            <span className="desktop-float-right pr-3">
                                {cellProps.value < 0 ? negativeValue : formatCurrency(absValue, 2)}
                            </span>
                        );
                    }, []),
                width: 150,
            },
            {
                Header: '',
                accessor: 'menu',
                Cell: (cellProps: {
                    row: {
                        original: { amId: number; source: string; documentHeaderId: number };
                    };
                }) =>
                    useMemo(() => {
                        if (INVOICE_TRANSACTION_SOURCES.includes(cellProps.row.original.source)) {
                            return (
                                <ProjectInvoiceModal
                                    toggleButton={
                                        <Button variant="link">
                                            <i className="fa fa-chevron-right" />
                                        </Button>
                                    }
                                    amId={amId}
                                    amNumber={props.amNumber}
                                    invoiceId={cellProps.row.original.documentHeaderId}
                                    commitment={false}
                                />
                            );
                        }
                        return null;
                    }, []),
                disableSortBy: true,
                width: 50,
            },
        ],
        [],
    );

    const initialStateObj = JSON.parse(initialState);

    return loading ? (
        <Spinner />
    ) : (
        <>
            <ResponseError errorMessage={error} />
            <div className="col-12 py-2">
                <div className="row">
                    <div className="col-2 col-overview-label">Total Actuals</div>
                    <div className="col-2 col-overview-label">
                        {props.total != null && formatCurrency(props.total)}
                    </div>
                    <div className="col-8 text-right">
                        <Button
                            variant="outline-primary"
                            onClick={() => {
                                window.open(
                                    `${tableauDashboardUrl}/views/ProjectOverviewNavigationDetail/NetActualsDetail?:iid=1&Project%20Number=${amNumber}`,
                                    '_blank',
                                );
                            }}
                            size="sm"
                        >
                            <i className="far fa-external-link-square-alt" /> Go to Tableau
                        </Button>
                        <Button
                            variant="outline-primary"
                            className="ml-1"
                            disabled={loading || !tableRefReady || actuals.length <= 0}
                            onClick={() => {
                                if (tableInstanceRef.current) {
                                    exportActualsList(
                                        tableInstanceRef.current.rows.map((row) => row.original),
                                        `Project ${amNumber} Actuals.csv`,
                                    );
                                }
                            }}
                            size="sm"
                        >
                            <i className="fas fa-file-export" /> Export CSV
                        </Button>
                    </div>
                </div>
            </div>
            <ActualsFilter
                setSearchObject={(newObject: Object) => {
                    setInitialState(
                        JSON.stringify({
                            ...initialStateObj,
                            pageIndex: 0,
                        }),
                    );
                    setSearch(JSON.stringify(newObject));
                }}
                searchString={search}
                searching={loading}
                onSubmit={refreshFunc}
            />
            <div className="react-table p-2">
                <DataTable<ProjectActualItem>
                    columns={columns as Column<ProjectActualItem>[]}
                    data={actuals}
                    initialState={initialStateObj}
                    setInitialState={setInitialState}
                    noGlobalFilter
                    getInstanceCallback={(tableInstance) => {
                        if (tableInstanceRef) {
                            tableInstanceRef.current = tableInstance;
                            setTableRefReady(true);
                        }
                    }}
                />
            </div>
        </>
    );
};

export default Actuals;
